import { Controller } from '@hotwired/stimulus';
import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';

export default class extends Controller {
  static targets = ['menu', 'trigger'];
  static values = {
    placement: { type: String, default: 'bottom' },
    maxWidth: { type: String, default: 'none' },
    autoClose: { type: Boolean, default: false },
    transform: String
  };

  connect() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('turbo:click', this.handleTurboClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    this.cleanup = null;
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('turbo:click', this.handleTurboClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    this.removeAutoCloseListeners();
    this.clearHideTimeout();
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
  }

  clearHideTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  toggle = (event) => {
    event.preventDefault();
    if (this.menuTarget.classList.contains('hidden')) {
      this.show(event.currentTarget);
    } else {
      this.hide({ trigger: event.currentTarget });
    }
  }

  handleKeydown = (event) => {
    // Handle Enter and Space keys for keyboard accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle(event);
    }
  }

  show(button) {
    this.clearHideTimeout();
    this.menuTarget.classList.remove('hidden');
    this.menuTarget.classList.add('visible');
    this.setTriggerExpanded(button, true);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    const item = this.element.closest('.photos__item');
    const transform = this.hasTransformValue
      ? this.transformValue : this.menuTarget.style.transform || '';

    if (item) item.classList.add('photos__item--active');

    if (this.cleanup) {
      this.cleanup();
    }

    if (this.autoCloseValue) {
      this.addAutoCloseListeners();
    }

    const placement = this.placementValue || 'bottom';

    this.cleanup = autoUpdate(button, this.menuTarget, async () => {
      const middleware = [offset(8), flip(), shift()];
      const { x, y } = await computePosition(button, this.menuTarget, {
        placement,
        middleware,
        strategy: 'fixed',
      });

      // Measure content width for dynamic sizing
      this.menuTarget.style.width = 'auto';
      this.menuTarget.style.maxWidth = 'none';
      const contentWidth = this.menuTarget.scrollWidth;

      // Calculate width with padding
      const calculatedWidth = contentWidth + 10;

      // Apply styles with calculated width
      Object.assign(this.menuTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: 'fixed',
        width: `${calculatedWidth}px`,
        maxWidth: this.maxWidthValue,
        transform: transform
      });
    });

    this.dispatch('shown');
  }

  hide = ({ restoreFocus = false, trigger = this.activeTrigger } = {}) => {
    const wasVisible = !this.menuTarget.classList.contains('hidden');

    this.clearHideTimeout();
    this.menuTarget.classList.add('hidden');
    this.menuTarget.classList.remove('visible');
    this.setTriggerExpanded(trigger, false);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    const item = this.element.closest('.photos__item');
    if (item) item.classList.remove('photos__item--active');

    this.removeAutoCloseListeners();

    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }

    // Only restore focus on keyboard close; outside clicks already moved focus
    // somewhere the user chose.
    if (restoreFocus && trigger && trigger.isConnected) {
      trigger.focus();
    }

    // Skip the event on no-op hides (e.g. outside-click while already hidden).
    if (wasVisible) this.dispatch('hidden');
  }

  // Hides only when the event came from an ancestor (e.g. a filter chip
  // clearing). Unrelated dropdowns on the page are unaffected.
  hideIfOwner(event) {
    if (!event.target || !event.target.contains(this.element)) return;
    this.hide();
  }

  // Closes in response to a bubbled request from the panel's content (e.g.
  // Enter on a filter option commits and closes). Restores focus to the
  // trigger so keyboard users land back on the chip.
  closeFromContent() {
    this.hide({ restoreFocus: true });
  }

  setTriggerExpanded(trigger, expanded) {
    if (trigger) {
      this.activeTrigger = expanded ? trigger : null;
      trigger.setAttribute('aria-expanded', expanded.toString());
      return;
    }

    if (!this.hasTriggerTarget) return;

    this.triggerTargets.forEach((triggerTarget) => {
      triggerTarget.setAttribute('aria-expanded', expanded.toString());
    });
  }

  addAutoCloseListeners() {
    this.removeAutoCloseListeners();
    this.element.addEventListener('mouseleave', this.handleMouseLeave);
    this.menuTarget.addEventListener('mouseleave', this.handleMenuMouseLeave);
    this.menuTarget.addEventListener('mouseenter', this.handleMenuMouseEnter);
    this._autoCloseListenersAttached = true;
  }

  removeAutoCloseListeners() {
    if (!this._autoCloseListenersAttached) return;
    this.element.removeEventListener('mouseleave', this.handleMouseLeave);
    if (this.hasMenuTarget) {
      this.menuTarget.removeEventListener('mouseleave', this.handleMenuMouseLeave);
      this.menuTarget.removeEventListener('mouseenter', this.handleMenuMouseEnter);
    }
    this._autoCloseListenersAttached = false;
  }

  handleOutsideClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.hide();
    }
  }

  // Esc closes the panel, even over a focused search input. defaultPrevented
  // lets inner handlers (e.g. a modal on top) opt out. preventDefault is
  // scoped to when focus is inside our menu so we override the native Escape
  // (e.g. clearing a focused search input) without poisoning sibling open
  // dropdowns; calling preventDefault unconditionally would set
  // defaultPrevented for the rest of the document listeners and leave any
  // other open dropdown stuck open on the same keystroke.
  handleDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (event.defaultPrevented) return;
    if (!this.hasMenuTarget) return;
    if (this.menuTarget.classList.contains('hidden')) return;

    const restoreFocus = this.menuTarget.contains(document.activeElement);
    if (restoreFocus) event.preventDefault();

    this.hide({ restoreFocus });
  }

  handleTurboClick = (event) => {
    const link = event.target.closest("a[data-turbo-frame='modal']");
    if (link && this.element.contains(link)) {
      this.hide();
    }
  }

  handleMouseLeave = (event) => {
    // Check if the mouse is moving to the menu
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && this.menuTarget.contains(relatedTarget)) {
      return;
    }
    // Use a small timeout to allow moving to the menu
    this.hideTimeout = setTimeout(() => {
      this.hide();
    }, 100);
  }

  handleMenuMouseLeave = (event) => {
    // Check if the mouse is moving back to the trigger element
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && this.element.contains(relatedTarget)) {
      return;
    }
    this.hide();
  }

  handleMenuMouseEnter = () => {
    // Cancel any pending hide when entering the menu
    this.clearHideTimeout();
  }
}
